import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useText } from "../contexts/TextsContext";
import { trackFormSubmit, trackPhoneClick, trackEmailClick } from "../lib/tracking";

export default function Contact() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const t = useText();

  useEffect(() => {
    api.get("/site-content/contact").then(r => setC(r.data.value)).catch(() => {});
  }, []);

  const cc = c || { phone: "02 38 74 50 60", email: "contact@immobiliersolaire.fr", city: "CERCOTTES", address: "98 Route Nationale 20, 45520 Cercottes", hours: "Lun–Ven 9h–18h · Visites sur RDV", maps_embed: "https://www.google.com/maps?q=Cercottes,+45520,+France&output=embed" };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-requests", { ...form, company: form.company || null });
      trackFormSubmit("contact", { has_company: !!form.company });
      toast.success(t("contact.success"));
      setForm({ full_name: "", email: "", phone: "", company: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-4">{t("contact.kicker")}</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4">{t("contact.title")}</h1>
        <p className="text-slate-600 max-w-2xl mb-12">{t("contact.subtitle")}</p>

        <div className="grid lg:grid-cols-2 gap-8" data-testid="contact-page">
          {/* Coordonnées + form */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-900/15 p-8">
              <h2 className="font-display font-bold text-2xl mb-6">{t("contact.coords_title")}</h2>
              <div className="space-y-5 text-sm">
                <div className="flex gap-4">
                  <MapPin className="w-5 h-5 text-[#FFC107] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold uppercase text-xs tracking-widest text-slate-500 mb-1">{t("contact.addr_label")}</div>
                    <div className="text-slate-800" data-testid="contact-address">{cc.address}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{t("contact.city_prefix")} {cc.city}</div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone className="w-5 h-5 text-[#FFC107] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold uppercase text-xs tracking-widest text-slate-500 mb-1">{t("contact.phone_label")}</div>
                    <a href={`tel:${(cc.phone || "").replace(/\s/g,"")}`} onClick={() => trackPhoneClick(cc.phone)} className="text-slate-800 hover:underline font-mono text-lg" data-testid="contact-phone">{cc.phone}</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Mail className="w-5 h-5 text-[#FFC107] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold uppercase text-xs tracking-widest text-slate-500 mb-1">{t("contact.email_label")}</div>
                    <a href={`mailto:${cc.email}`} onClick={() => trackEmailClick(cc.email)} className="text-slate-800 hover:underline" data-testid="contact-email">{cc.email}</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock className="w-5 h-5 text-[#FFC107] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold uppercase text-xs tracking-widest text-slate-500 mb-1">{t("contact.hours_label")}</div>
                    <div className="text-slate-800">{cc.hours}</div>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="bg-white border border-slate-900/15 p-8 space-y-4" data-testid="contact-form">
              <h2 className="font-display font-bold text-2xl mb-2">{t("contact.form_title")}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <input required placeholder={t("contact.form.name_ph")} value={form.full_name} onChange={set("full_name")} className="input" data-testid="cf-name" />
                <input required type="email" placeholder={t("contact.form.email_ph")} value={form.email} onChange={set("email")} className="input" data-testid="cf-email" />
                <input required placeholder={t("contact.form.phone_ph")} value={form.phone} onChange={set("phone")} className="input" data-testid="cf-phone" />
                <input placeholder={t("contact.form.company_ph")} value={form.company} onChange={set("company")} className="input" />
              </div>
              <textarea required rows={4} placeholder={t("contact.form.message_ph")} value={form.message} onChange={set("message")} className="input resize-none" data-testid="cf-message" />
              <button disabled={submitting} type="submit" data-testid="cf-submit"
                className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 disabled:opacity-50">
                {submitting ? t("global.cta_sending") : t("global.cta_send")} {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </div>

          {/* Maps + visuel */}
          <div className="space-y-6">
            <div className="border border-slate-900/15 bg-white overflow-hidden">
              <iframe
                src={cc.maps_embed}
                className="w-full h-[400px] block"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Carte Google Maps"
                data-testid="contact-map"
              />
            </div>
            <div className="bg-slate-900 text-white p-8">
              <h3 className="font-display font-bold text-xl mb-3">{t("contact.access.title")}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{t("contact.access.desc")}</p>
              <Link to="/demande" data-testid="contact-cta-demande" className="btn-brutal btn-brutal-solar inline-flex items-center gap-2 mt-6 px-5 py-3 text-sm font-bold uppercase tracking-wider">
                {t("global.cta_request")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
